import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getCategoryIdFromCanonicalFilters } from 'src/redux/productsQuery/utils/getCategoryIdFromCanonicalFilters';
import { PageType } from 'src/types/navigation';
import { getTaxonomyLevelFromPathname } from 'src/utils/page';

export interface PageTrackingContext {
  entityName?: string;
  pageIdentifier?: string;
  pageType: PageType | null;
}

export const getCategoryPageIdentifier = (state: ReduxApplicationState): string | undefined => {
  const id = getCategoryIdFromCanonicalFilters(state.productsQuery.canonicalFilters);
  return id ? String(id) : undefined;
};

const getPathname = (state: ReduxApplicationState): string =>
  state.router?.location?.pathname ||
  (typeof window !== 'undefined' ? window.location.pathname : '');

export const getTaxonomyPageIdentifier = (state: ReduxApplicationState): string =>
  getTaxonomyLevelFromPathname(getPathname(state));

export const getSearchPageIdentifier = (): string => '15000';

export const getPartnerPageIdentifier = (state: ReduxApplicationState): string | undefined =>
  state.content.partner?.id ? String(state.content.partner.id) : undefined;

const pageIdentifierExtractors: Partial<
  Record<PageType, (state: ReduxApplicationState) => string | undefined>
> = {
  [PageType.Category]: getCategoryPageIdentifier,
  [PageType.Taxonomy]: getTaxonomyPageIdentifier,
  [PageType.Search]: getSearchPageIdentifier,
  [PageType.Partner]: getPartnerPageIdentifier,
};

export const getPageIdentifier = (state: ReduxApplicationState): string | undefined => {
  const pageType = state.navigation.pageType;
  if (!pageType) {
    return undefined;
  }

  const extractor = pageIdentifierExtractors[pageType as PageType];
  return extractor ? extractor(state) : undefined;
};

export const getSearchEntityName = (state: ReduxApplicationState): string | undefined =>
  state.productsQuery.searchTerm || undefined;

export const getPartnerEntityName = (state: ReduxApplicationState): string | undefined =>
  state.content.partner?.name || undefined;

const entityNameExtractors: Partial<
  Record<PageType, (state: ReduxApplicationState) => string | undefined>
> = {
  [PageType.Search]: getSearchEntityName,
  [PageType.Partner]: getPartnerEntityName,
};

export const getEntityName = (state: ReduxApplicationState): string | undefined => {
  const pageType = state.navigation.pageType;
  if (!pageType) {
    return undefined;
  }

  const extractor = entityNameExtractors[pageType as PageType];
  return extractor ? extractor(state) : undefined;
};

export const getPageTrackingContext = (state: ReduxApplicationState): PageTrackingContext => ({
  pageType: state.navigation.pageType,
  pageIdentifier: getPageIdentifier(state),
  entityName: getEntityName(state),
});
